import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useEvent, eventVars } from './EventProvider';

const translations = {
  en: {
    tagline: 'Rooted · Growing · Serving',
    nav: { gathering: 'The Gathering', flyer: 'Flyer', watch: 'Watch', sermons: 'Sermons', studies: 'Studies', prayer: 'Prayer', vision: 'Vision', rsvp: 'RSVP', give: 'Give' },
    hero: {
      badge: "You're Invited",
      title_1: 'Planted to grow,', title_2: 'rooted in love.',
      sub: 'A new church family is putting down roots. Join us for our next gathering at {campus} — {name}, a worship service, and Bible study, all in one gathering. Come as you are.',
      cta: 'RSVP for {wdMd}', watchCta: 'Discover · Grow · Serve',
      date: '{dayShort}', dateSub: 'Our next gathering',
      time: '{time}', timeSub: 'Free coffee & doughnuts',
      coffee: '{campus}', coffeeSub: '{venueShort} · {room}',
      scroll: 'Scroll',
    },
    gathering: {
      eyebrow: 'One Morning, Three Gatherings',
      title_1: 'Come for the morning,', title_2: 'stay for the family.',
      sub: 'Our very first gathering brings everything together in one welcoming morning — Coffee & Conversation, a worship service, and a Bible study. All are welcome. No experience with church required.',
      cards: [
        { title: 'Coffee & Conversation', text: "Coffee. Conversation. The Word. Community. A relaxed, welcoming time for real conversations and genuine community — whether you're new to faith, returning after time away, or just looking for a place to belong. You don't have to have all the answers to pull up a chair." },
        { title: 'Worship Service', text: 'A simple, sincere time of worship and a message of hope. No matter your background, you belong in this room.' },
        { title: 'Bible Study', text: "We'll open the Word together and discover what it means to be planted by streams of water (Psalm 1:3)." },
      ],
      coffeeTitle: 'FREE Coffee & Doughnuts',
      coffeeText: "Arrive a few minutes early and say hi. Free coffee, warm doughnuts, and real conversation — that's how every Planted morning begins.",
      whenTitle: '{dateLong} · {time}',
      whenText: "{venue} · {room} · {campus}, {address}. Bring a friend, bring your kids, bring your questions. We'll save you a seat.",
    },
    flyer: {
      eyebrow: 'Save the Date', title: "You're invited.",
      sub: 'Share our flyer with a friend, send it in a text, or print it for your community board — every detail about the next two Sundays is right here.',
      download: 'Download flyer', share: 'Share', copied: 'Copied',
      tipLabel: 'Tip:', tip: 'Save the image and post it in a group chat or on social.',
      toastTitle: 'Link copied', toastText: "Share with a friend — we'll save them a seat.",
    },
    visitor: {
      eyebrow: 'New here?', title_1: 'Complete your', title_2: 'visitor card.',
      sub: 'So we can welcome you well and keep you in the loop, share a little info with us. Email is required so we can follow up; your mobile number is optional.',
      name: 'Name', email: 'Email address', mobile: 'Mobile number', optional: '(optional)',
      note: "Anything you'd like us to know?",
      notePh: "How you found us, questions, or how you'd like to get involved",
      submit: 'Submit visitor card', sending: 'Sending…',
      okTitle: 'Welcome to Planted', okText: "We've got your info — we'll be in touch soon.",
      requiredTitle: 'Just a moment', requiredText: 'Name and email are required so we can follow up.',
      yourName: 'Your name', emailPh: 'you@email.com',
    },
    rsvp: {
      eyebrow: 'Reserve your seat', title_1: 'RSVP for', title_2: '{wdMd}.',
      sub: "Let us know you're coming so we can save you a seat at the {venue} on {dateLong} at {time}. It takes less than a minute \u2014 and if you share your email, we\u2019ll send you a friendly reminder the day before.",
      qrTitle: 'Scan to RSVP', qrText: 'Point your phone camera here to open this page and RSVP from anywhere.',
      walkin: "Prefer to just show up? You're always welcome — no RSVP needed.",
      name: 'Name', email: 'Email', phone: 'Phone', count: 'Number attending',
      kids: 'Bringing children', note: "Anything you'd like us to know?",
      notePh: 'Dietary needs, questions, or a note for the team',
      submit: 'Count me in', sending: 'Sending…',
      okTitle: 'Count you in', okText: "See you {wdMd} at {time} \u2014 {venueShort}, {room}.",
      requiredTitle: 'Just your name', requiredText: 'Please share your name so we can save you a seat.',
      optional: '(optional)',
    },
    dgs: {
      eyebrow: 'Our Heartbeat', title_1: 'Discover. Grow.', title_2: 'Serve.',
      sub: "These three words shape everything we do. Long before we ever gather in person, you can journey with us on YouTube — teaching, encouragement, and a glimpse of who we're becoming together.",
      cards: [
        { title: 'Discover', text: 'Who Jesus is and what it means to follow Him — no jargon, no pressure, just honest seeking.' },
        { title: 'Grow', text: 'Roots going deep through Scripture, prayer, and community. Real life change, one step at a time.' },
        { title: 'Serve', text: "Love made tangible. We bless our city with the gifts and grace we've been given." },
      ],
      ytTitle: 'Watch on YouTube', ytText: 'Subscribe to Discover Grow Serve and grow with us from anywhere.',
      ytBtn: 'Visit channel',
    },
    prayer: {
      eyebrow: 'Pray With Us', title_1: 'A community that', title_2: 'prays together.',
      sub: "Prayer is where we Discover God's heart, Grow together, and Serve one another in love. Bring your request — Pastor Greg & Sheena, and this whole community, will pray along with you.",
      formTitle: 'Share a prayer request',
      formSub: 'Your name stays between you and the pastors unless you choose to share.',
      yourName: 'Your name', connect: 'How does this connect with your journey?',
      request: 'Your request', requestPh: "Share what's on your heart…",
      shareLabel: 'Share with the community so others can pray along too.',
      shareHint: '(Uncheck to keep this private with Pastor Greg & Sheena.)',
      submit: 'Submit prayer request',
      wallTitle: 'Praying together', wallSub: 'Join Pastor Greg & Sheena in praying for these requests.',
      praying: "I'm praying",
      live: 'Live',
      okShared: 'Request shared', okSharedText: 'The community is lifting you up.',
      okPrivate: 'Received privately', okPrivateText: 'Pastor Greg & Sheena will be praying.',
      missing: 'Add a request', missingText: 'Please share what we can pray for.',
      optional: '(optional)', anonymous: 'Anonymous',
      newBadge: 'New',
    },
    vision: {
      eyebrow: "Built for what's next", title_1: 'From a seed to a', title_2: 'sheltering tree.',
      sub: 'Planted Ministries is designed for the long haul. This first gathering is just the beginning — a church rooted in Scripture, growing in love, and reaching outward for years to come.',
      cards: [
        { title: 'Planted', text: 'We begin as a seed — a small gathering of people trusting God for something new. Every great movement of God starts with a few, planted in faith.' },
        { title: 'Growing', text: 'Roots deepen and branches reach. We grow in Christ together through worship, the Word, and genuine community — not perfect people, just people being made new.' },
        { title: 'Bearing Fruit', text: 'A tree planted by streams of water bears fruit in season. We exist to bless our city, multiply disciples, and plant more churches in the years to come.' },
      ],
      quote: '“He is like a tree planted by streams of water, which yields its fruit in season and whose leaf does not wither.”',
      quoteRef: 'Psalm 1:3',
      statements: [
        { label: 'Our Vision', text: 'To see people planted in Christ, rooted in His Word, growing in faith, and serving others with purpose.' },
        { label: 'Our Mission', text: 'We help people discover who Jesus is, what He has done, and what it means to follow Him. We believe that everyone should have the opportunity to hear the Gospel, encounter the presence of God, experience salvation, and discover their identity in Christ.' },
      ],
    },
    shareCard: {
      download: 'Download vision card', share: 'Share vision', hint: 'Save or share our vision with a friend.',
      okTitle: 'Vision card ready', okText: 'Your image has been downloaded.',
      shareTitle: 'Planted Ministries — Our Vision',
    },
    values: {
      eyebrow: 'What we value', title_1: 'Roots that', title_2: 'hold us steady.',
      sub: 'These core values flow from our vision and mission. They shape how we worship, how we grow, and how we love our city.',
      items: [
        { title: 'Christ at the Center', text: 'Jesus is the root of everything. We exist to help people discover who He is and what He has done.' },
        { title: 'Rooted in the Word', text: "Scripture is our foundation. We open God's Word together and let it shape our lives." },
        { title: 'The Presence of God', text: 'We make room for people to encounter God personally through worship and prayer.' },
        { title: 'The Gospel for Everyone', text: 'Everyone deserves the opportunity to hear the Good News and experience salvation.' },
        { title: 'Identity in Christ', text: 'We help people discover who they are in Christ — loved, redeemed, and called.' },
        { title: 'Serving with Purpose', text: 'Faith grows hands and feet. We serve others and bless our city with intention.' },
      ],
    },
    pastors: {
      eyebrow: 'Meet our Pastor & his wife', title_1: 'Pastor Greg', title_2: '& Sheena',
      bio: [
        'Pastor Greg and Sheena carry a heart to see people planted in Christ and rooted in His Word. Planted Ministries was born out of prayer and a simple desire: to help people discover who Jesus is and what it means to follow Him.',
        "Whether you've walked with God for years or you're just curious, they would love to meet you, hear your story, and pray with you. Come as you are — there's a seat saved for you.",
      ],
      signature: 'Pastor Greg & Sheena', role: 'Founding Pastors',
      photoSoon: 'Photo coming soon', cta: 'Say hello',
    },
    nextSteps: {
      eyebrow: 'Find your next step', title_1: 'Wherever you are,', title_2: "there's a next step.",
      sub: "Faith is a journey, not a jump. Choose what you're interested in and Pastor Greg & Sheena will personally reach out to help you take it.",
      options: [
        { id: 'bible_study', title: 'Join a Bible study', text: "Open God's Word in community." },
        { id: 'serve', title: 'Serve on a team', text: 'Use your gifts to bless others.' },
        { id: 'baptism', title: 'Get baptized', text: 'Publicly declare your faith in Jesus.' },
        { id: 'prayer', title: 'Prayer / counseling', text: 'Talk and pray with a pastor.' },
        { id: 'learn', title: 'Learn about Jesus', text: 'Explore who He is, no pressure.' },
      ],
      pick: 'What would you like to explore?', name: 'Name', email: 'Email', phone: 'Phone', optional: '(optional)',
      note: 'Anything else we should know?', notePh: 'Questions, availability, or a little about your story',
      yourName: 'Your name', emailPh: 'you@email.com',
      submit: 'Take my next step', sending: 'Sending…',
      okTitle: "We're so glad", okText: "Thank you! We'll reach out soon to walk this next step with you.",
      requiredTitle: 'Almost there', requiredText: 'Please add your name, email, and choose at least one step.',
    },
    clock: {
      back: { title: 'Clocks fall back this weekend', text: 'Set your clocks back 1 hour. We start at {time} new time on {wdMd}.' },
      forward: { title: 'Clocks spring forward this weekend', text: 'Set your clocks ahead 1 hour. We start at {time} new time on {wdMd}.' },
    },
    invite: {
      button: 'Invite a friend', title: 'Bring a friend along', sub: 'Edit the message below, then send it in one tap.',
      message: "Hey! I'm going to {name} at Planted Ministries on {dateLong} at {time} \u2014 {place}. Free coffee & doughnuts, real conversation, and no pressure. Want to come with me? RSVP here: {url}",
      subject: 'Join me at {name} \u2014 {dateLong}',
      text: 'Text', email: 'Email', whatsapp: 'WhatsApp', copy: 'Copy', copied: 'Copied!',
      copiedText: 'Invite copied \u2014 paste it anywhere.',
      doneTitle: "You're in!", doneText: "We saved your seat for {wdMd} at {time}. Who would you love to bring along?",
      another: 'RSVP for someone else',
    },
    give: {
      eyebrow: 'Give', title_1: 'Plant your gift in', title_2: 'what God is growing.',
      sub: 'Your tithes and offerings fuel everything you see here — the launch, the gatherings, the prayers, and the love poured out on our city. Every gift, great or small, helps put down roots that will last.',
      cta: 'Give via Cash App', thanks: 'Thank you for partnering with Planted Ministries.',
    },
    footer: {
      about: "A new church plant, rooted in God's Word and growing to serve our city. Come plant your roots with us.",
      join: 'Join Us', connect: 'Connect With Us',
      when: '{dateLong} · {time}',
      lineup: 'Coffee & Conversation · Worship · Bible Study',
      coffee: '{venueShort} · {address}',
      rsvpLink: 'RSVP for {wdMd} →',
      channel: 'Discover Grow Serve',
      rights: 'All rights reserved.',
      planted: 'Planted with', forCity: 'for our city',
    },
    tags: { General: 'General', Discover: 'Discover', Grow: 'Grow', Serve: 'Serve' },
  },
  es: {
    tagline: 'Enraizados · Creciendo · Sirviendo',
    nav: { gathering: 'La Reunión', flyer: 'Volante', watch: 'Ver', sermons: 'Sermones', studies: 'Estudios', prayer: 'Oración', vision: 'Visión', rsvp: 'Confirmar', give: 'Ofrendar' },
    hero: {
      badge: 'Estás Invitado',
      title_1: 'Plantados para crecer,', title_2: 'arraigados en amor.',
      sub: 'Una nueva familia de iglesia está echando raíces. Únete a nuestra próxima reunión en {campus}: {name}, un servicio de adoración y un estudio bíblico, todo en una sola reunión. Ven tal como eres.',
      cta: 'Confirmar para el {wdMd}', watchCta: 'Descubre · Crece · Sirve',
      date: '{dayShort}', dateSub: 'Nuestra próxima reunión',
      time: '{time}', timeSub: 'Café y donas gratis',
      coffee: '{campus}', coffeeSub: '{venueShort} · {room}',
      scroll: 'Desplazar',
    },
    gathering: {
      eyebrow: 'Una mañana, tres encuentros',
      title_1: 'Ven por la mañana,', title_2: 'quédate por la familia.',
      sub: 'Nuestra primera reunión lo reúne todo en una sola mañana acogedora: Café y Conversación, un servicio de adoración y un estudio bíblico. Todos son bienvenidos. No se necesita experiencia previa en la iglesia.',
      cards: [
        { title: 'Café y Conversación', text: 'Café. Conversación. La Palabra. Comunidad. Un tiempo relajado y acogedor para conversaciones reales y comunidad genuina, ya seas nuevo en la fe, estés regresando después de un tiempo o simplemente busques un lugar al cual pertenecer. No necesitas tener todas las respuestas para acercar una silla.' },
        { title: 'Servicio de adoración', text: 'Un tiempo sencillo y sincero de adoración y un mensaje de esperanza. Sin importar tu trasfondo, perteneces en esta sala.' },
        { title: 'Estudio Bíblico', text: 'Abriremos la Palabra juntos y descubriremos lo que significa ser plantados junto a corrientes de agua (Salmo 1:3).' },
      ],
      coffeeTitle: 'Café y donas GRATIS',
      coffeeText: 'Llega unos minutos antes y saluda. Café gratis, donas calientes y conversación real: así comienza cada mañana en Planted.',
      whenTitle: '{dateLong} · {time}',
      whenText: '{venue} · {room} · {campus}, {address}. Trae a un amigo, a tus hijos y tus preguntas. Te guardaremos un lugar.',
    },
    flyer: {
      eyebrow: 'Reserva la fecha', title: 'Estás invitado.',
      sub: 'Comparte nuestro volante con un amigo, envíalo por mensaje o imprimelo para tu barrio: todos los detalles de los próximos dos domingos están aquí.',
      download: 'Descargar volante', share: 'Compartir', copied: 'Copiado',
      tipLabel: 'Consejo:', tip: 'Guarda la imagen y publícala en un chat grupal o en redes sociales.',
      toastTitle: 'Enlace copiado', toastText: 'Compártelo con un amigo — le guardaremos un lugar.',
    },
    visitor: {
      eyebrow: '¿Eres nuevo?', title_1: 'Completa tu', title_2: 'tarjeta de visitante.',
      sub: 'Para darte una buena bienvenida y mantenerte al tanto, compártenos un poco de información. El correo es obligatorio para poder responderte; el celular es opcional.',
      name: 'Nombre', email: 'Correo electrónico', mobile: 'Número de celular', optional: '(opcional)',
      note: '¿Algo que quieras contarnos?',
      notePh: 'Cómo nos encontraste, preguntas o cómo te gustaría involucrarte',
      submit: 'Enviar tarjeta', sending: 'Enviando…',
      okTitle: 'Bienvenido a Planted', okText: 'Recibimos tu información — pronto te contactaremos.',
      requiredTitle: 'Un momento', requiredText: 'El nombre y el correo son obligatorios para poder responderte.',
      yourName: 'Tu nombre', emailPh: 'tu@correo.com',
    },
    rsvp: {
      eyebrow: 'Reserva tu lugar', title_1: 'Confirma tu asistencia al', title_2: '{dateLongLower}.',
      sub: 'Avísanos que vienes para guardarte un lugar en el {venue} el {dateLongLower} a las {time}. Toma menos de un minuto, y si compartes tu correo te enviaremos un recordatorio amable el día anterior.',
      qrTitle: 'Escanea para confirmar', qrText: 'Apunta la cámara de tu teléfono aquí para abrir esta página y confirmar desde cualquier lugar.',
      walkin: '¿Prefieres solo presentarte? Siempre eres bienvenido — no se necesita confirmación.',
      name: 'Nombre', email: 'Correo', phone: 'Teléfono', count: 'Personas que asistirán',
      kids: 'Vendrán con niños', note: '¿Algo que quieras contarnos?',
      notePh: 'Necesidades alimentarias, preguntas o una nota para el equipo',
      submit: 'Cuenta conmigo', sending: 'Enviando…',
      okTitle: 'Te esperamos', okText: 'Nos vemos el {dateLongLower} a las {time} — {venueShort}, {room}.',
      requiredTitle: 'Solo tu nombre', requiredText: 'Compártenos tu nombre para guardarte un lugar.',
      optional: '(opcional)',
    },
    dgs: {
      eyebrow: 'Nuestro Latido', title_1: 'Descubre. Crece.', title_2: 'Sirve.',
      sub: 'Estas tres palabras moldean todo lo que hacemos. Mucho antes de reunirnos en persona, puedes caminar con nosotros en YouTube: enseñanza, aliento y un vistazo de quiénes nos estamos convirtiendo juntos.',
      cards: [
        { title: 'Descubre', text: 'Quién es Jesús y lo que significa seguirlo: sin jerga, sin presión, solo una búsqueda honesta.' },
        { title: 'Crece', text: 'Raíces profundas a través de la Escritura, la oración y la comunidad. Vidas transformadas, paso a paso.' },
        { title: 'Sirve', text: 'Amor hecho tangible. Bendecimos nuestra ciudad con los dones y la gracia que hemos recibido.' },
      ],
      ytTitle: 'Ver en YouTube', ytText: 'Suscríbete a Discover Grow Serve y crece con nosotros desde donde estés.',
      ytBtn: 'Visitar canal',
    },
    prayer: {
      eyebrow: 'Ora con nosotros', title_1: 'Una comunidad que', title_2: 'ora unida.',
      sub: 'La oración es donde Descubrimos el corazón de Dios, Crecemos juntos y nos Servimos unos a otros en amor. Trae tu petición: el Pastor Greg y Sheena, y toda esta comunidad, orarán contigo.',
      formTitle: 'Comparte una petición',
      formSub: 'Tu nombre queda entre tú y los pastores a menos que decidas compartirlo.',
      yourName: 'Tu nombre', connect: '¿Cómo se conecta esto con tu caminar?',
      request: 'Tu petición', requestPh: 'Comparte lo que tienes en el corazón…',
      shareLabel: 'Comparte con la comunidad para que otros también puedan orar.',
      shareHint: '(Desmarca para mantenerlo privado con el Pastor Greg y Sheena.)',
      submit: 'Enviar petición',
      wallTitle: 'Orando juntos', wallSub: 'Únete al Pastor Greg y Sheena orando por estas peticiones.',
      praying: 'Estoy orando',
      live: 'En vivo',
      okShared: 'Petición compartida', okSharedText: 'La comunidad está orando por ti.',
      okPrivate: 'Recibida en privado', okPrivateText: 'El Pastor Greg y Sheena estarán orando.',
      missing: 'Falta la petición', missingText: 'Compártenos por qué quieres que oremos.',
      optional: '(opcional)', anonymous: 'Anónimo',
      newBadge: 'Nuevo',
    },
    vision: {
      eyebrow: 'Hecho para lo que viene', title_1: 'De una semilla a un', title_2: 'árbol de refugio.',
      sub: 'Planted Ministries está diseñado para el largo plazo. Esta primera reunión es solo el comienzo: una iglesia arraigada en la Escritura, creciendo en amor y alcanzando a otros por muchos años.',
      cards: [
        { title: 'Plantados', text: 'Comenzamos como una semilla: un pequeño grupo confiando en Dios para algo nuevo. Todo gran mover de Dios comienza con unos pocos, plantados en fe.' },
        { title: 'Creciendo', text: 'Las raíces se profundizan y las ramas se extienden. Crecemos juntos en Cristo mediante la adoración, la Palabra y una comunidad genuina.' },
        { title: 'Dando fruto', text: 'Un árbol plantado junto a corrientes de agua da fruto a su tiempo. Existimos para bendecir nuestra ciudad, multiplicar discípulos y plantar más iglesias.' },
      ],
      quote: '«Será como árbol plantado junto a corrientes de aguas, que da su fruto en su tiempo, y su hoja no cae.»',
      quoteRef: 'Salmo 1:3',
      statements: [
        { label: 'Nuestra Visión', text: 'Ver a las personas plantadas en Cristo, arraigadas en Su Palabra, creciendo en la fe y sirviendo a los demás con propósito.' },
        { label: 'Nuestra Misión', text: 'Ayudamos a las personas a descubrir quién es Jesús, lo que Él ha hecho y lo que significa seguirle. Creemos que todos deben tener la oportunidad de escuchar el Evangelio, experimentar la presencia de Dios, recibir la salvación y descubrir su identidad en Cristo.' },
      ],
    },
    shareCard: {
      download: 'Descargar tarjeta', share: 'Compartir visión', hint: 'Guarda o comparte nuestra visión con un amigo.',
      okTitle: 'Tarjeta lista', okText: 'Tu imagen se ha descargado.',
      shareTitle: 'Planted Ministries — Nuestra Visión',
    },
    values: {
      eyebrow: 'Lo que valoramos', title_1: 'Raíces que', title_2: 'nos sostienen.',
      sub: 'Estos valores fluyen de nuestra visión y misión. Moldean cómo adoramos, cómo crecemos y cómo amamos a nuestra ciudad.',
      items: [
        { title: 'Cristo en el centro', text: 'Jesús es la raíz de todo. Existimos para ayudar a las personas a descubrir quién es Él y lo que ha hecho.' },
        { title: 'Arraigados en la Palabra', text: 'La Escritura es nuestro fundamento. Abrimos juntos la Palabra de Dios y dejamos que moldee nuestras vidas.' },
        { title: 'La presencia de Dios', text: 'Hacemos espacio para que las personas se encuentren con Dios a través de la adoración y la oración.' },
        { title: 'El Evangelio para todos', text: 'Todos merecen la oportunidad de escuchar las Buenas Nuevas y recibir la salvación.' },
        { title: 'Identidad en Cristo', text: 'Ayudamos a las personas a descubrir quiénes son en Cristo: amados, redimidos y llamados.' },
        { title: 'Servir con propósito', text: 'La fe tiene manos y pies. Servimos a otros y bendecimos nuestra ciudad con intención.' },
      ],
    },
    pastors: {
      eyebrow: 'Conoce a nuestro pastor y su esposa', title_1: 'Pastor Greg', title_2: 'y Sheena',
      bio: [
        'El Pastor Greg y Sheena tienen el corazón de ver a las personas plantadas en Cristo y arraigadas en Su Palabra. Planted Ministries nació en oración y con un deseo sencillo: ayudar a las personas a descubrir quién es Jesús y lo que significa seguirle.',
        'Ya sea que hayas caminado con Dios por años o simplemente tengas curiosidad, les encantaría conocerte, escuchar tu historia y orar contigo. Ven tal como eres: hay un lugar guardado para ti.',
      ],
      signature: 'Pastor Greg y Sheena', role: 'Pastores fundadores',
      photoSoon: 'Foto próximamente', cta: 'Saluda',
    },
    nextSteps: {
      eyebrow: 'Encuentra tu próximo paso', title_1: 'Dondequiera que estés,', title_2: 'hay un próximo paso.',
      sub: 'La fe es un camino, no un salto. Elige lo que te interesa y el Pastor Greg y Sheena se comunicarán contigo personalmente para ayudarte.',
      options: [
        { id: 'bible_study', title: 'Unirme a un estudio bíblico', text: 'Abre la Palabra de Dios en comunidad.' },
        { id: 'serve', title: 'Servir en un equipo', text: 'Usa tus dones para bendecir a otros.' },
        { id: 'baptism', title: 'Bautizarme', text: 'Declara públicamente tu fe en Jesús.' },
        { id: 'prayer', title: 'Oración / consejería', text: 'Habla y ora con un pastor.' },
        { id: 'learn', title: 'Conocer a Jesús', text: 'Descubre quién es Él, sin presión.' },
      ],
      pick: '¿Qué te gustaría explorar?', name: 'Nombre', email: 'Correo', phone: 'Teléfono', optional: '(opcional)',
      note: '¿Algo más que debamos saber?', notePh: 'Preguntas, disponibilidad o un poco de tu historia',
      yourName: 'Tu nombre', emailPh: 'tu@correo.com',
      submit: 'Dar mi próximo paso', sending: 'Enviando…',
      okTitle: 'Nos alegra mucho', okText: '¡Gracias! Pronto nos comunicaremos para acompañarte en este paso.',
      requiredTitle: 'Casi listo', requiredText: 'Agrega tu nombre, correo y elige al menos un paso.',
    },
    clock: {
      back: { title: 'Este fin de semana se atrasa la hora', text: 'Atrasa tu reloj 1 hora. Empezamos a las {time} (nuevo horario) el {dateLongLower}.' },
      forward: { title: 'Este fin de semana se adelanta la hora', text: 'Adelanta tu reloj 1 hora. Empezamos a las {time} (nuevo horario) el {dateLongLower}.' },
    },
    invite: {
      button: 'Invitar a un amigo', title: 'Trae a un amigo', sub: 'Edita el mensaje y envíalo con un toque.',
      message: '¡Hola! Voy a {name} en Planted Ministries el {dateLongLower} a las {time} \u2014 {place}. Café y donas gratis, conversación real y sin presión. ¿Quieres venir conmigo? Confirma aquí: {url}',
      subject: 'Acompáñame a {name} \u2014 {dateLong}',
      text: 'Mensaje', email: 'Correo', whatsapp: 'WhatsApp', copy: 'Copiar', copied: '¡Copiado!',
      copiedText: 'Invitación copiada \u2014 pégala donde quieras.',
      doneTitle: '¡Listo!', doneText: 'Te guardamos un lugar para el {dateLongLower} a las {time}. ¿A quién te encantaría traer?',
      another: 'Confirmar para otra persona',
    },
    give: {
      eyebrow: 'Ofrendar', title_1: 'Siembra tu ofrenda en', title_2: 'lo que Dios está haciendo crecer.',
      sub: 'Tus diezmos y ofrendas sostienen todo lo que ves aquí: el lanzamiento, las reuniones, las oraciones y el amor derramado sobre nuestra ciudad. Cada ofrenda, grande o pequeña, ayuda a echar raíces duraderas.',
      cta: 'Ofrendar por Cash App', thanks: 'Gracias por colaborar con Planted Ministries.',
    },
    footer: {
      about: 'Una nueva iglesia plantada, arraigada en la Palabra de Dios y creciendo para servir a nuestra ciudad. Ven a plantar tus raíces con nosotros.',
      join: 'Únete', connect: 'Conectáte con nosotros',
      when: '{dateLong} · {time}',
      lineup: 'Café y Conversación · Adoración · Estudio Bíblico',
      coffee: '{venueShort} · {address}',
      rsvpLink: 'Confirmar para el {mdLong} →',
      channel: 'Discover Grow Serve',
      rights: 'Todos los derechos reservados.',
      planted: 'Plantado con', forCity: 'para nuestra ciudad',
    },
    tags: { General: 'General', Discover: 'Descubre', Grow: 'Crece', Serve: 'Sirve' },
  },
};

const fill = (node, vars) => {
  if (typeof node === 'string') return node.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
  if (Array.isArray(node)) return node.map((n) => fill(n, vars));
  if (node && typeof node === 'object') return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, fill(v, vars)]));
  return node;
};

const I18nContext = createContext({ lang: 'en', t: translations.en, setLang: () => {} });

export function I18nProvider({ children }) {
  const { event } = useEvent();
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('planted_lang') || 'en'; } catch (_) { return 'en'; }
  });
  useEffect(() => {
    try { localStorage.setItem('planted_lang', lang); } catch (_) { /* ignore */ }
    document.documentElement.lang = lang;
  }, [lang]);
  const t = useMemo(() => fill(translations[lang], eventVars(event, lang)), [lang, event]);
  const value = { lang, t, setLang };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() { return useContext(I18nContext); }
